import type { ArrowDefinition } from '../model/types';

/**
 * 盤面セルに整列したレーン格子の 1 本。座標はすべてセル単位（1 = 1 セル）で、
 * ワールド座標への変換は描画側（arrowRenderer）が cellSize を掛けて行う。
 */
export interface LaneSegment {
  /** 線が乗るセル境界。垂直線なら x、水平線なら y。 */
  readonly line: number;
  /** 線に沿った区間 [from, to]。 */
  readonly from: number;
  readonly to: number;
}

export interface LaneGrid {
  readonly vertical: readonly LaneSegment[];
  readonly horizontal: readonly LaneSegment[];
}

/**
 * レベルが実際に占めているセルだけを縁取るレーン格子を作る。
 *
 * 盤面の外接矩形へ一律に方眼を引かないのは、ADR-0007 のシルエット盤面
 * （矩形でない盤面）で、遊べない余白まで格子で囲ってしまい、シルエットの
 * 輪郭が読めなくなるため。占有セルの境界だけを引けば、外周の輪郭は
 * レーン線そのものとして自然に現れる。
 *
 * 隣接セル間の境界は 1 本に統合し、同じ線分を二度描かない（半透明で焼くため、
 * 重ね描きするとその辺だけ濃く出てしまう）。
 */
export function buildLaneGrid(
  arrows: readonly ArrowDefinition[],
  width: number,
  height: number,
): LaneGrid {
  const occupied = new Uint8Array(Math.max(0, width * height));
  for (const arrow of arrows) {
    for (const cell of arrow.cells) {
      if (cell.x >= 0 && cell.x < width && cell.y >= 0 && cell.y < height) {
        occupied[cell.y * width + cell.x] = 1;
      }
    }
  }
  const isOccupied = (x: number, y: number): boolean =>
    x >= 0 && x < width && y >= 0 && y < height && occupied[y * width + x] === 1;

  return {
    vertical: collectRuns(width, height, (line, index) =>
      isOccupied(line - 1, index) || isOccupied(line, index)),
    horizontal: collectRuns(height, width, (line, index) =>
      isOccupied(index, line - 1) || isOccupied(index, line)),
  };
}

/** 各境界線ごとに、辺が立っている連続区間を 1 本の線分へまとめる。 */
function collectRuns(
  lineCount: number,
  spanCount: number,
  hasEdge: (line: number, index: number) => boolean,
): LaneSegment[] {
  const segments: LaneSegment[] = [];
  for (let line = 0; line <= lineCount; line += 1) {
    let runStart: number | null = null;
    for (let index = 0; index <= spanCount; index += 1) {
      const filled = index < spanCount && hasEdge(line, index);
      if (filled && runStart === null) {
        runStart = index;
      } else if (!filled && runStart !== null) {
        segments.push({ line, from: runStart, to: index });
        runStart = null;
      }
    }
  }
  return segments;
}
